import React, { useState, useEffect } from "react";
import { Event, EventType } from "@visitproof/types";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Button } from "../../../design/button";
import { Field, Fieldset, FieldGroup, Label } from "../../../design/fieldset";
import { Textarea } from "../../../design/textarea";
import { Select } from "../../../design/select";
import { EventService } from "../../../services/EventService";
import { useAuth } from "../../../contexts/AuthContext";

interface EditEventDialogProps {
  event: Event;
  isOpen: boolean;
  onClose: () => void;
  onEventUpdated: () => void;
}

const EditEventDialog: React.FC<EditEventDialogProps> = ({
  event,
  isOpen,
  onClose,
  onEventUpdated,
}) => {
  const { user } = useAuth();
  const [body, setBody] = useState(event.body || "");
  const [type, setType] = useState<EventType>(event.type);
  const [severity, setSeverity] = useState(event.severity || "low");
  const [actionTaken, setActionTaken] = useState(event.actionTaken || "");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBody(event.body || "");
    setType(event.type);
    setSeverity(event.severity || "low");
    setActionTaken(event.actionTaken || "");
  }, [event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSaving(true);
    setError(null);

    try {
      await EventService.updateEvent(
        event.id,
        {
          body,
          type,
          severity,
          actionTaken: actionTaken || undefined,
        },
        user.uid
      );

      onEventUpdated();
      onClose();
    } catch (err) {
      console.error("Error updating event:", err);
      setError(err instanceof Error ? err.message : "Failed to update event");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setBody(event.body || "");
    setType(event.type);
    setSeverity(event.severity || "low");
    setActionTaken(event.actionTaken || "");
    setError(null);
    onClose();
  };

  // Determine if this is a system event that cannot be edited
  const isSystemEvent = [
    "supervisor_started",
    "supervisor_ended",
    "supervisor_arrived",
    "supervisor_enroute",
    "supervisor_no_show",
  ].includes(event.type);

  return (
    <Dialog open={isOpen} onClose={handleCancel} size="lg">
      <DialogTitle>Edit Event</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Event Type</Label>
                <Select
                  name="type"
                  value={type}
                  onChange={(e) => setType(e.target.value as EventType)}
                  disabled={isSystemEvent}
                >
                  <option value="observation">Observation</option>
                  <option value="interaction">Interaction</option>
                  <option value="incident">Incident</option>
                  <option value="intervention">Intervention</option>
                  <option value="phone_call">Phone Call</option>
                  <option value="party_arrived">Arrival</option>
                  <option value="handoff_note">Handoff Notes</option>
                  {isSystemEvent && (
                    <>
                      <option value="supervisor_started">Visit Started</option>
                      <option value="supervisor_ended">Visit Ended</option>
                      <option value="supervisor_arrived">Arrived</option>
                      <option value="supervisor_enroute">En Route</option>
                      <option value="supervisor_no_show">No Show</option>
                    </>
                  )}
                </Select>
              </Field>

              {!isSystemEvent && (
                <Field>
                  <Label>Severity</Label>
                  <Select
                    name="severity"
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value as "low" | "medium" | "high")}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </Select>
                </Field>
              )}

              <Field>
                <Label>Details</Label>
                <Textarea
                  name="body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  placeholder="Describe what happened..."
                  disabled={isSystemEvent}
                />
              </Field>

              {!isSystemEvent && (
                <Field>
                  <Label>Action Taken (Optional)</Label>
                  <Textarea
                    name="actionTaken"
                    value={actionTaken}
                    onChange={(e) => setActionTaken(e.target.value)}
                    rows={3}
                    placeholder="What action was taken in response..."
                  />
                </Field>
              )}
            </FieldGroup>
          </Fieldset>

          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {isSystemEvent && (
            <div className="mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Note: System events have limited editing. You can only update the details field.
              </p>
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditEventDialog;
